import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { useMessageBus } from "@jti/fe-message-bus";
import { areBundleOptionsFromCart, ICartItemErrorType, usePdpLink } from "@jti/magento";
import { useSelector } from "@jti/store/reduxHooks";
import { useTranslations } from "@jti/ui";

import { ProductInfoAndActions } from "@checkout/components/CartItem/ProductInfoAndActions/ProductInfoAndActions";
import { usePromotionalOffers } from "@checkout/hooks";
import { hasCartErrorCode } from "@checkout/utils/getCartErrors";

import type { BundleItemProps } from "../BundleItem";
import { computeQuantityOrderLimit } from "../utils/computeQuantityOrderLimit";

import CompactBundleOption from "./CompactBundleOption";

import styles from "./CompactBundleItem.module.scss";

export const CompactBundleItem: FC<BundleItemProps> = ({
  qtyPending = false,
  item,
  blockQuantityAdd = false,
  onRemoveClick,
  onQuantityChange,
}) => {
  const { __ } = useTranslations();
  const {
    id,
    sku,
    name,
    quantity,
    price,
    status,
    enable_order_quantity_limit,
    order_quantity_limit,
    promotionalOffers,
    has_registration_consent,
    subscription = 0,
    imageUrl,
    tradeIn,
  } = item;
  const messageBus = useMessageBus();
  const [limitMessageStatus, setLimitMessageStatus] = useState(false);
  const [isQuantityLimitReached, setIsQuantityLimitReached] = useState(false);
  const grandTotalPrice = useSelector((state) => state.cart.prices.data?.grandTotal.value) ?? 0;
  const shippingPrice = useSelector((state) => state.cart.prices.data?.shipping?.value) ?? 0;
  const maxOrderLimit = useSelector((state) => state.cart.storeConfig?.order_limit_value);
  const orderLimitEnabled = useSelector((state) => state.cart.storeConfig?.is_order_limit_enabled);
  const { SHOP_URL, PDP_URL } = useConfig();
  const pdpLink = usePdpLink(sku, { shopUrl: SHOP_URL, pdpUrl: PDP_URL ?? "" });
  const isOutOfStock =
    (item.errors && hasCartErrorCode(item.errors, ICartItemErrorType.ItemQty)) || status === "OUT_OF_STOCK";
  const {
    isLimitReached: isPromotionalOfferLimitReached,
    hasPromotionalOffers,
    promotionalOfferLimit,
  } = usePromotionalOffers(quantity, promotionalOffers);
  const isSubscriptionItem = subscription === 1;
  const quantityOrderLimit = computeQuantityOrderLimit({
    hasPromotionalOffers,
    promotionalOfferLimit,
    tradeIn,
    enable_order_quantity_limit,
    order_quantity_limit,
  });

  useEffect(() => {
    const skuLimitReached = quantity >= order_quantity_limit && enable_order_quantity_limit === 1;
    const specialOfferLimitReached = hasPromotionalOffers && isPromotionalOfferLimitReached;

    setIsQuantityLimitReached(skuLimitReached || specialOfferLimitReached);

    messageBus.publish("cart.limit.update", {
      payload: {
        sku,
        quantity,
        limit: quantityOrderLimit.quantityLimit,
        quantityLimitReached: skuLimitReached || specialOfferLimitReached,
      },
    });
  }, [
    enable_order_quantity_limit,
    hasPromotionalOffers,
    isPromotionalOfferLimitReached,
    order_quantity_limit,
    quantity,
  ]);

  useEffect(() => {
    setLimitMessageStatus(grandTotalPrice + price.value / quantity - shippingPrice > maxOrderLimit);
  }, [grandTotalPrice, price, shippingPrice, maxOrderLimit, quantity]);

  const handleOnRemove = useCallback(() => {
    onRemoveClick(id);

    messageBus.publish("cart.limit.update", {
      payload: {
        sku,
        quantity: 0,
        limit: quantityOrderLimit.quantityLimit,
        quantityLimitReached: false,
      },
    });
  }, [id, sku, quantityOrderLimit.quantityLimit, onRemoveClick]);

  const handleQuantityChange = useCallback(
    (value: number) => {
      onQuantityChange(id, value);
    },
    [id, onQuantityChange]
  );

  const isAddDisabled = blockQuantityAdd || isQuantityLimitReached;

  return (
    <div className={styles.container}>
      <ProductInfoAndActions
        url={pdpLink}
        name={name}
        imageUrl={imageUrl}
        outOfStock={isOutOfStock}
        price={price}
        isSubscription={isSubscriptionItem}
        hasRegistrationConsent={has_registration_consent}
        quantity={{
          value: quantity,
          disabled: isOutOfStock || qtyPending,
          disabledAdd: isAddDisabled,
          max: quantityOrderLimit.isLimit === 1 ? quantityOrderLimit.quantityLimit : undefined,
          title: isOutOfStock ? __("cart.cart_item.out_of_stock") : undefined,
          onChange: handleQuantityChange,
          orderLimitEnabled,
          isOrderLimitReached: limitMessageStatus,
          maxOrderLimit,
          currency: price.currency,
        }}
        removeItemHandler={handleOnRemove}
        isQuantityLimitReached={isQuantityLimitReached}
        quantityLimit={quantityOrderLimit.quantityLimit}
      />
      <ul className={styles.bundleOptionsContainer}>
        {item?.bundle_options?.map(
          ({ values, uid, label }) =>
            values &&
            areBundleOptionsFromCart(values) && (
              <CompactBundleOption key={`${sku} - ${label} - ${uid}}`} categoryName={label} values={values} />
            )
        )}
      </ul>
    </div>
  );
};
